import React, { useState } from 'react';
import styled from 'styled-components';
import { ModalOverlay, ModalContainer } from './BaseModal';
import { gradePromotionPreview, promoteGrades, getGradeLabel } from '../../api/studentPortalApi';

/**
 * Grade +1 (2026-09-29): every student of one campus, including inactive, moves up one
 * year level; Year 12 becomes Graduated (and inactive). Once per campus per academic year.
 * Closes only via X / Cancel.
 */

const CONFIRM_TEXT = 'GRADE +1';
const defaultAcademicYear = () => {
  const now = new Date();
  return now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear(); // Oct-Dec: next year
};

const Modal = styled(ModalContainer)`max-width: 720px;`;
const Head = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid #e5e7eb;
  h3 { margin: 0; font-size: 18px; }
`;
const Close = styled.button`
  background: none; border: none; font-size: 24px; color: #6b7280; cursor: pointer;
  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;
const Body = styled.div`padding: 16px 20px; overflow-y: auto; flex: 1;`;
const Foot = styled.div`display: flex; justify-content: flex-end; gap: 8px; padding: 12px 20px; border-top: 1px solid #e5e7eb;`;
const Field = styled.label`
  display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; font-size: 14px; font-weight: 500;
  select, input { padding: 8px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; }
`;
const Btn = styled.button`
  padding: 8px 16px; border-radius: 6px; border: none; cursor: pointer; font-size: 14px;
  background: ${p => p.$danger ? '#dc2626' : p.$secondary ? '#e5e7eb' : '#F1AF59'};
  color: ${p => p.$secondary ? '#111827' : 'white'};
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const Warn = styled.div`background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; border-radius: 8px; padding: 12px; margin-bottom: 12px; font-size: 14px;`;
const Info = styled.div`background: #eff6ff; border: 1px solid #bfdbfe; color: #1e3a8a; border-radius: 8px; padding: 12px; margin-bottom: 12px; font-size: 14px;`;
const List = styled.div`max-height: 280px; overflow-y: auto; font-size: 13px; border: 1px solid #f3f4f6; border-radius: 6px; padding: 8px; margin-bottom: 12px;`;

const GradePromotionModal = ({ campuses, onClose, onDone }) => {
  const [campusId, setCampusId] = useState(campuses[0]?.id || '');
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear());
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const loadPreview = async () => {
    setBusy(true);
    setError('');
    try {
      setPreview(await gradePromotionPreview(campusId, academicYear));
      setConfirmText('');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    setBusy(true);
    setError('');
    try {
      setResult(await promoteGrades(campusId, academicYear));
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const movers = (preview?.students || []).filter(s => s.grade !== null && s.newGrade !== Number(s.grade));

  return (
    <ModalOverlay>
      <Modal>
        <Head>
          <h3>Grade +1</h3>
          <Close onClick={onClose} disabled={busy}>&times;</Close>
        </Head>
        <Body>
          {error && <Warn>{error}</Warn>}
          {result ? (
            <Info>Done: {result.promoted} students moved up ({result.graduated} graduated), {result.skipped} unchanged.</Info>
          ) : (
            <>
              {campuses.length > 1 && (
                <Field>Campus
                  <select value={campusId} onChange={e => { setCampusId(e.target.value); setPreview(null); }}>
                    {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </Field>
              )}
              <Field>New school year
                <select value={academicYear} onChange={e => { setAcademicYear(Number(e.target.value)); setPreview(null); }}>
                  {[defaultAcademicYear() - 1, defaultAcademicYear(), defaultAcademicYear() + 1].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </Field>
              {preview && preview.alreadyDone && (
                <Warn>Grade +1 has already been run for {academicYear} at this campus. It cannot be run again.</Warn>
              )}
              {preview && !preview.alreadyDone && (
                <>
                  <Warn>
                    <strong>This cannot be undone.</strong> {movers.length} of {preview.total} students (including inactive)
                    move up one year level. {preview.graduating} Year 12 student(s) become Graduated and are set to inactive.
                  </Warn>
                  <List>
                    {movers.map(s => (
                      <div key={s.id}>{s.name}: {getGradeLabel(s.grade)} → {getGradeLabel(s.newGrade)}</div>
                    ))}
                  </List>
                  <Field>Type <strong>{CONFIRM_TEXT}</strong> to confirm
                    <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder={CONFIRM_TEXT} />
                  </Field>
                </>
              )}
            </>
          )}
        </Body>
        <Foot>
          {result ? (
            <Btn $secondary onClick={onClose}>Close</Btn>
          ) : (
            <>
              <Btn $secondary onClick={onClose} disabled={busy}>Cancel</Btn>
              {!preview && <Btn onClick={loadPreview} disabled={!campusId || busy}>{busy ? 'Loading...' : 'Review'}</Btn>}
              {preview && !preview.alreadyDone && (
                <Btn $danger onClick={run} disabled={busy || confirmText.trim().toUpperCase() !== CONFIRM_TEXT}>
                  {busy ? 'Processing...' : 'Confirm Grade +1'}
                </Btn>
              )}
            </>
          )}
        </Foot>
      </Modal>
    </ModalOverlay>
  );
};

export default GradePromotionModal;
